import { Button, Tooltip } from 'antd';
import demoPodcast from 'assets/images/demo-podcast.png';
import React, { useEffect, useState } from 'react';
import { useAppSelector } from '@app/hooks/reduxHooks';
import { IDealRoomShare } from '@app/interfaces/deal-room';
import {
  getPodcastImageSrc,
  getPodcastsByTypeAuth,
  getPodcastsByTypePublic,
  IPodcast,
  PodcastType,
} from '@app/api/podcast.api';
import * as S from './ListenToOurPodcast.styles';
import { Spinner } from '@app/components/common/Spinner/Spinner';

/**
 * Font Awesome 5 brands "spotify" (CC BY 4.0) — the same glyph baroz.co.il
 * renders inside its episode buttons via `fab fa-spotify`.
 */
const SpotifyIcon: React.FC = () => (
  <svg viewBox="0 0 496 512" aria-hidden="true" focusable="false">
    <path d="M248 8C111.1 8 0 119.1 0 256s111.1 248 248 248 248-111.1 248-248S384.9 8 248 8zm100.7 364.9c-4.2 0-6.8-1.3-10.7-3.6-62.4-37.6-135-39.2-206.7-24.5-3.9 1-9 2.6-11.9 2.6-9.7 0-15.8-7.7-15.8-15.8 0-10.3 6.1-15.2 13.6-16.8 81.9-18.1 165.6-16.5 237 26.2 6.1 3.9 9.7 7.4 9.7 16.5s-7.1 15.4-15.2 15.4zm26.9-65.6c-5.2 0-8.7-2.3-12.3-4.2-62.5-37-155.7-51.9-238.6-29.4-4.8 1.3-7.4 2.6-11.9 2.6-10.7 0-19.4-8.7-19.4-19.4s5.2-17.8 15.5-20.7c27.8-7.8 56.2-13.6 97.8-13.6 64.9 0 127.6 16.1 177 45.5 8.1 4.8 11.3 11 11.3 19.7-.1 10.8-8.5 19.5-19.4 19.5zm31-76.2c-5.2 0-8.4-1.3-12.9-3.9-71.2-42.5-198.5-52.7-280.9-29.7-3.6 1-8.1 2.6-12.9 2.6-13.2 0-23.3-10.3-23.3-23.6 0-13.6 8.4-21.3 17.4-23.9 35.2-10.3 74.6-15.2 117.5-15.2 73 0 149.5 15.2 205.4 47.8 7.8 4.5 12.9 10.7 12.9 22.6 0 13.6-11 23.3-23.2 23.3z" />
  </svg>
);

interface ListenToOurPodcastProps {
  type?: PodcastType;
  dealRoom?: IDealRoomShare;
}

/**
 * Extracts the video id from the two YouTube link shapes the podcast records
 * use: youtu.be/<id> and youtube.com/watch?v=<id>. Returns undefined for
 * anything else, which keeps the episode on its artwork with no play button.
 */
const getYoutubeEmbedUrl = (videoUrl?: string): string | undefined => {
  if (!videoUrl) return undefined;
  try {
    const url = new URL(videoUrl);
    const id = url.hostname.includes('youtu.be') ? url.pathname.replace(/^\//, '') : url.searchParams.get('v');
    return id ? `https://www.youtube.com/embed/${id}?autoplay=1&rel=0` : undefined;
  } catch {
    return undefined;
  }
};

export const ListenToOurPodcast: React.FC<ListenToOurPodcastProps> = ({ type, dealRoom }) => {
  const user = useAppSelector((state) => state.user.user);
  const [podcasts, setPodcasts] = useState<IPodcast[]>([]);
  const [loading, setLoading] = useState(false);
  // Id of the episode whose player has been opened; only one plays at a time.
  const [playingId, setPlayingId] = useState<string | null>(null);
  // Episodes whose artwork failed to load, so they render like the placeholder.
  const [brokenImageIds, setBrokenImageIds] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    if (!type) return;

    const load = async () => {
      setLoading(true);
      try {
        const data = user
          ? await getPodcastsByTypeAuth(type)
          : dealRoom?.id
          ? await getPodcastsByTypePublic(dealRoom.id, type, {
              sessionId: dealRoom.sessionId,
              uniqueId: dealRoom.uniqueId,
            })
          : [];
        if (!cancelled) setPodcasts(data);
      } catch {
        if (!cancelled) setPodcasts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [type, user, dealRoom?.id, dealRoom?.sessionId, dealRoom?.uniqueId]);

  return (
    <S.Container>
      {/* Injects the overlay styling for the portal-rendered tooltips below. */}
      <S.PodcastTooltipStyle />
      <S.List>
        {loading && <Spinner />}
        {!loading &&
          podcasts.map((item) => {
            const embedUrl = getYoutubeEmbedUrl(item.videoUrl);
            const isPlaying = playingId === item.id && Boolean(embedUrl);
            const artwork = brokenImageIds.includes(item.id) ? undefined : getPodcastImageSrc(item);

            return (
              <S.Card key={item.id}>
                <S.CardContent>
                  <S.MediaWrapper $isPlaying={isPlaying} $hasArtwork={Boolean(artwork)}>
                    {isPlaying ? (
                      <iframe
                        src={embedUrl}
                        title={item.title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <>
                        <img
                          src={artwork || demoPodcast}
                          alt={item.title}
                          loading="lazy"
                          decoding="async"
                          onError={() =>
                            setBrokenImageIds((prev) => (prev.includes(item.id) ? prev : [...prev, item.id]))
                          }
                        />
                        {/* No video means the artwork stands alone — no play affordance. */}
                        {embedUrl && (
                          <S.PlayButton type="button" onClick={() => setPlayingId(item.id)} aria-label="נגן וידאו">
                            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </S.PlayButton>
                        )}
                      </>
                    )}
                  </S.MediaWrapper>
                  <S.TextWrapper>
                    {/* Speaker badge — the same chip the reference site shows above its titles. */}
                    {item.tag && <S.Tag>{item.tag}</S.Tag>}
                    {/* Both lines are clamped to three rows, so the tooltip is
                        the only place the full text can be read. */}
                    <Tooltip title={item.title} placement="top" overlayClassName="podcast-tooltip">
                      <S.Title>{item.title}</S.Title>
                    </Tooltip>
                    {item.description && (
                      <Tooltip title={item.description} placement="bottom" overlayClassName="podcast-tooltip">
                        <S.Description>{item.description}</S.Description>
                      </Tooltip>
                    )}
                  </S.TextWrapper>
                  {item.podcastLink && (
                    <S.ButtonWrapper>
                      <Button type="default" href={item.podcastLink} target="_blank" rel="noopener noreferrer">
                        <SpotifyIcon />
                        להאזנה
                      </Button>
                    </S.ButtonWrapper>
                  )}
                </S.CardContent>
              </S.Card>
            );
          })}
      </S.List>
    </S.Container>
  );
};
